// Island routes (Crumble Cake, a shredded Teacup rim): a shortest path over the safety field's
// ground cells where a bean can also hop a gap of up to `maxGap` meters in a straight line. Used
// when the straight way to a goal crosses the void: the route's first leg is either a walk to a
// waypoint or a hop to a landing cell. Dijkstra with small integer edge costs (a bucket queue),
// allocation-free after the first call; a few hundred microseconds on the 0.5 m Cake grid.
import type { SafetyField } from './safetyField';

/** The first leg of a route. */
export interface Leg {
  /** Waypoint (walk) or landing point (hop), meters. */
  x: number;
  y: number;
  /** The leg is a hop over a gap: sprint at it and jump at the brink. */
  hop: boolean;
}

const DX = [1, 1, 0, -1, -1, -1, 0, 1];
const DY = [0, 1, 1, 1, 0, -1, -1, -1];
const BUCKETS = 32;
const NONE = -1;

export class RoutePlanner {
  private cost = new Int32Array(0);
  private prev = new Int32Array(0);
  private hopEdge = new Uint8Array(0);
  private buckets: number[][] = Array.from({ length: BUCKETS }, () => []);

  /**
   * Plans from (sx, sy) to the ground cell nearest (tx, ty). Ground is safety ≥ `minSafety`; hops
   * cross void cells in one of 8 directions and land at least `landSafety` deep, at most `maxGap`
   * of void in between. Writes the first leg into `out`; false if the goal is unreachable.
   */
  route(f: SafetyField, sx: number, sy: number, tx: number, ty: number, minSafety: number, landSafety: number, maxGap: number, out: Leg): boolean {
    const nx = f.nx;
    const ny = f.ny;
    const n = nx * ny;
    if (n === 0) return false;
    if (this.cost.length !== n) {
      this.cost = new Int32Array(n);
      this.prev = new Int32Array(n);
      this.hopEdge = new Uint8Array(n);
    }
    const start = f.cellAt(sx, sy);
    const goal = f.cellAt(tx, ty);
    if (start < 0 || goal < 0) return false;
    const d = f.d;
    const ground = (k: number): boolean => d[k] >= minSafety && f.block[k] === 0;
    this.cost.fill(0x7fffffff);
    this.prev.fill(NONE);
    for (const b of this.buckets) b.length = 0;
    this.cost[start] = 0;
    this.buckets[0].push(start);
    const maxHop = Math.max(2, Math.ceil(maxGap / f.cell) + 1);
    let found = NONE;
    let bestGoalD = 1e9;
    let pending = 1;
    for (let c = 0; pending > 0 && c < n * 4; c++) {
      const bucket = this.buckets[c % BUCKETS];
      while (bucket.length > 0) {
        const k = bucket.pop() as number;
        pending--;
        if (this.cost[k] !== c) continue;
        const i = k % nx;
        const j = (k - i) / nx;
        const gd = Math.hypot(i - (goal % nx), j - Math.floor(goal / nx));
        if (gd < bestGoalD && ground(k)) {
          bestGoalD = gd;
          found = k;
          if (gd === 0) break;
        }
        for (let dir = 0; dir < 8; dir++) {
          const diag = dir & 1;
          // Walk to a neighbor.
          const ii = i + DX[dir];
          const jj = j + DY[dir];
          if (ii >= 0 && jj >= 0 && ii < nx && jj < ny) {
            const kk = jj * nx + ii;
            if (ground(kk)) {
              const nc = c + 2 + diag;
              if (nc < this.cost[kk]) {
                this.cost[kk] = nc;
                this.prev[kk] = k;
                this.hopEdge[kk] = 0;
                this.buckets[nc % BUCKETS].push(kk);
                pending++;
              }
              continue;
            }
          }
          // Hop: void cells in a straight line, then ground deep enough to land on.
          for (let s = 2; s <= maxHop; s++) {
            const hi = i + DX[dir] * s;
            const hj = j + DY[dir] * s;
            if (hi < 0 || hj < 0 || hi >= nx || hj >= ny) break;
            const hk = hj * nx + hi;
            if (f.block[hk] === 1) break;
            if (d[hk] < landSafety) continue;
            const nc = c + s * (2 + diag) + 2;
            if (nc - c < BUCKETS && nc < this.cost[hk]) {
              this.cost[hk] = nc;
              this.prev[hk] = k;
              this.hopEdge[hk] = 1;
              this.buckets[nc % BUCKETS].push(hk);
              pending++;
            }
            break;
          }
        }
      }
      if (bestGoalD === 0) break;
    }
    if (found === NONE || found === start) return false;
    // Walk back to the first leg from the start.
    let k = found;
    let steps = 0;
    while (this.prev[k] !== start && this.prev[k] !== NONE) {
      k = this.prev[k];
      steps++;
      if (steps > n) return false;
    }
    if (this.hopEdge[k] === 1) {
      out.x = f.cellX(k);
      out.y = f.cellY(k);
      out.hop = true;
      return true;
    }
    // A walking first leg: aim a few cells down the path (not past the next hop's takeoff).
    let tip = found;
    let back = 0;
    const path: number[] = [];
    for (let q = found; q !== start && q !== NONE; q = this.prev[q]) path.push(q);
    path.reverse();
    for (const q of path) {
      if (this.hopEdge[q] === 1) break;
      tip = q;
      if (++back >= 4) break;
    }
    out.x = f.cellX(tip);
    out.y = f.cellY(tip);
    out.hop = false;
    return true;
  }
}
